import React from "react";
import { useNavigate } from "react-router-dom";
import { Agent } from "../../types/security";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TrendIndicator } from "./TrendIndicator";
import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";
import { AgentProviderLogo } from "./AgentProviderLogo";
import { useTenant } from "@/providers/TenantProvider";

interface AgentsListProps {
  agents: Agent[];
}

interface IssueCountProps {
  count: number;
  activeColor: string;
}

const IssueCount: React.FC<IssueCountProps> = ({ count, activeColor }) => {
  const colorClass = count > 0 ? activeColor : "text-gray-400";

  return <Badge variant="outline" className={`min-w-8 justify-center rounded-[5px] bg-background font-medium tabular-nums ${colorClass}`}>{count}</Badge>;
};

interface AgentRowProps {
  agent: Agent;
  onClick: () => void;
  tenantName?: string;
}

const AgentRow: React.FC<AgentRowProps> = React.memo(({ agent, onClick, tenantName }) => {
  return (
    <TableRow
      tabIndex={0}
      className="cursor-pointer transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      onClick={onClick}
      onKeyDown={(event) => { if (event.key === "Enter") onClick(); }}
    >
      <TableCell className="py-3">
        <div className="flex items-center gap-3">
          <AgentProviderLogo provider={agent.modelProvider} className="h-6 w-6 shrink-0" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm font-medium leading-none">{agent.name}</span>
            <span className="truncate text-xs leading-none text-muted-foreground">{agent.modelName}</span>
          </div>
        </div>
      </TableCell>
      {tenantName !== undefined && (
        <TableCell className="py-3 text-sm text-muted-foreground">{tenantName}</TableCell>
      )}
      <TableCell className="py-3 max-w-xs">
        <span className="text-sm text-muted-foreground line-clamp-2 leading-tight">
          {agent.description}
        </span>
      </TableCell>
      <TableCell className="py-3 text-center">
        <IssueCount count={agent.criticalIssues} activeColor="text-[hsl(var(--chart-critical))]" />
      </TableCell>
      <TableCell className="py-3 text-center">
        <IssueCount count={agent.highIssues} activeColor="text-[hsl(var(--chart-high))]" />
      </TableCell>
      <TableCell className="py-3 text-center">
        <div className="flex justify-center"><TrendIndicator direction={agent.trendDirection} percentage={agent.trendPercentage} /></div>
      </TableCell>
    </TableRow>
  );
});

AgentRow.displayName = "AgentRow";

export const AgentsList: React.FC<AgentsListProps> = ({ agents }) => {
  const navigate = useNavigate();
  const { isMssp, isAll, orgNameMap } = useTenant();
  const showTenantCol = isMssp && isAll;

  return (
    <div className="overflow-hidden rounded-[15px] border bg-card shadow-none">
      <div className="hidden md:block">
      <Table>
        <TableHeader>
          <TableRow className="h-9">
            <TableHead className="py-2 text-xs font-medium">Agent</TableHead>
            {showTenantCol && <TableHead className="py-2 text-xs font-medium">Tenant</TableHead>}
            <TableHead className="py-2 text-xs font-medium">Description</TableHead>
            <TableHead className="py-2 text-xs font-medium text-center">Critical</TableHead>
            <TableHead className="py-2 text-xs font-medium text-center">High</TableHead>
            <TableHead className="py-2 text-xs font-medium text-center">Trend</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {agents.map((agent) => (
            <AgentRow
              key={agent.id}
              agent={agent}
              onClick={() => navigate(`/agents/${agent.id}`)}
              tenantName={showTenantCol ? orgNameMap.get(agent.organizationId) : undefined}
            />
          ))}
        </TableBody>
      </Table>
      </div>
      <div className="divide-y md:hidden">
        {agents.map((agent) => (
          <button
            key={agent.id}
            onClick={() => navigate(`/agents/${agent.id}`)}
            className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <AgentProviderLogo provider={agent.modelProvider} className="h-6 w-6 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{agent.name}</p>
              <p className="truncate text-xs text-muted-foreground">{showTenantCol ? `${orgNameMap.get(agent.organizationId) ?? ""} · ` : ""}{agent.modelName} · {agent.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <IssueCount count={agent.criticalIssues} activeColor="text-[hsl(var(--chart-critical))]" />
              <IssueCount count={agent.highIssues} activeColor="text-[hsl(var(--chart-high))]" />
            </div>
            <ChevronRight className="size-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
};
